import React, { useState, useEffect } from 'react';
import analyticsFormulasService from '../../services/analyticsFormulasService';
import {
  BarChart,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const SocialMediaAnalyticsDashboard = ({ campaignData = [], loading = false, selectedCampaign = 'all' }) => {
  const [platformAnalytics, setPlatformAnalytics] = useState({
    instagram: {
      views: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      saves: 0,
      posts: 0,
      engagementRate: 0,
      trend: 0
    },
    facebook: {
      views: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      saves: 0,
      posts: 0,
      engagementRate: 0,
      trend: 0
    },
    tiktok: {
      views: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      saves: 0,
      posts: 0,
      engagementRate: 0,
      trend: 0
    }
  });

  const [previousMetrics, setPreviousMetrics] = useState({
    instagram: { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 },
    facebook: { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 },
    tiktok: { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 }
  });

  // Platform configurations
  const platformConfigs = {
    instagram: {
      name: 'Instagram',
      color: 'from-pink-500 to-purple-600',
      bgColor: 'bg-gradient-to-br from-pink-50 to-purple-50',
      textColor: 'text-pink-600',
      benchmark: 6.0 // Industry benchmark for excellent engagement
    },
    facebook: {
      name: 'Facebook',
      color: 'from-blue-500 to-blue-700',
      bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
      textColor: 'text-blue-600',
      benchmark: 0.9 // Industry benchmark for excellent engagement
    },
    tiktok: {
      name: 'TikTok',
      color: 'from-gray-800 to-black',
      bgColor: 'bg-gradient-to-br from-gray-50 to-gray-100',
      textColor: 'text-gray-800',
      benchmark: 9.0 // Industry benchmark for excellent engagement
    }
  };

  useEffect(() => {
    const calculatePlatformAnalytics = () => {
      if (!campaignData || campaignData.length === 0) return;

      // Filter campaigns based on selection
      const filteredCampaigns = selectedCampaign === 'all' 
        ? campaignData 
        : campaignData.filter(campaign => campaign.id === selectedCampaign);

      const newAnalytics = {
        instagram: { views: 0, likes: 0, comments: 0, shares: 0, saves: 0, posts: 0 },
        facebook: { views: 0, likes: 0, comments: 0, shares: 0, saves: 0, posts: 0 },
        tiktok: { views: 0, likes: 0, comments: 0, shares: 0, saves: 0, posts: 0 }
      };

      // Aggregate data from filtered campaigns
      filteredCampaigns.forEach(campaign => {
        if (campaign.socialMediaMetrics) {
          Object.keys(newAnalytics).forEach(platform => {
            const metrics = campaign.socialMediaMetrics[platform];
            if (metrics) {
              newAnalytics[platform].views += metrics.views || 0;
              newAnalytics[platform].likes += metrics.likes || 0;
              newAnalytics[platform].comments += metrics.comments || 0;
              newAnalytics[platform].shares += metrics.shares || 0;
              newAnalytics[platform].saves += metrics.saves || 0;
              newAnalytics[platform].posts += metrics.posts || 0;
            }
          });
        }
      });

      // Calculate engagement rates and trends
      const analyticsWithRates = {};
      Object.keys(newAnalytics).forEach(platform => {
        const data = newAnalytics[platform];
        const engagementRate = analyticsFormulasService.calculateEngagementRate(
          data.likes,
          data.comments,
          data.shares,
          data.saves,
          data.views
        );
        
        const trend = previousMetrics[platform].views > 0 
          ? ((data.views - previousMetrics[platform].views) / previousMetrics[platform].views) * 100 
          : 0;

        analyticsWithRates[platform] = {
          ...data,
          engagementRate: parseFloat(engagementRate.toFixed(2)),
          trend: parseFloat(trend.toFixed(1))
        };
      });

      setPlatformAnalytics(analyticsWithRates);
    };

    calculatePlatformAnalytics();
  }, [campaignData, previousMetrics]);

  // Trend indicator component
  const TrendIndicator = ({ current, previous }) => {
    // Simple trend calculation
    const trend = previous > 0 ? ((current - previous) / previous) * 100 : 0;
    
    return (
      <div className="flex items-center justify-center mt-1">
        {trend > 0 ? (
          <TrendingUp className="h-4 w-4 text-green-500" />
        ) : trend < 0 ? (
          <TrendingDown className="h-4 w-4 text-red-500" />
        ) : (
          <div className="h-4 w-4 bg-gray-400 rounded-full" />
        )}
        <span className={`ml-1 text-xs font-medium ${
          trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'
        }`}>
          {trend !== 0 ? `${trend > 0 ? '+' : ''}${trend.toFixed(1)}%` : '0%'}
        </span>
      </div>
    );
  };

  // Performance level indicator
  const getPerformanceLevel = (engagementRate, platform) => {
    // Simple performance level calculation
    const benchmarks = {
      instagram: { excellent: 6.0, good: 3.0, average: 1.0 },
      facebook: { excellent: 0.9, good: 0.5, average: 0.2 },
      tiktok: { excellent: 9.0, good: 5.0, average: 2.0 }
    };
    
    const benchmark = benchmarks[platform] || benchmarks.instagram;
    let level = 'Below Average';
    
    if (engagementRate >= benchmark.excellent) {
      level = 'Excellent';
    } else if (engagementRate >= benchmark.good) {
      level = 'Good';
    } else if (engagementRate >= benchmark.average) {
      level = 'Average';
    }
    
    const colors = {
      'Excellent': 'text-green-600 bg-green-100',
      'Good': 'text-blue-600 bg-blue-100',
      'Average': 'text-yellow-600 bg-yellow-100',
      'Below Average': 'text-red-600 bg-red-100'
    };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colors[level]}`}>
        {level}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Social Media Analytics</h2>
        <p className="text-gray-600">Engagement rates calculated using industry-standard formulas</p>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(platformAnalytics).map(([platform, data]) => {
          const config = platformConfigs[platform];
          const previousData = previousMetrics[platform];
          
          return (
            <div key={platform} className={`rounded-xl shadow-lg overflow-hidden ${config.bgColor}`}>
              {/* Platform Header */}
              <div className={`bg-gradient-to-r ${config.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">{config.name}</h3>
                    <p className="text-sm opacity-90">{data.posts} posts</p>
                  </div>
                  <BarChart className="h-8 w-8 opacity-80" />
                </div>
                
                {/* Engagement Rate Display */}
                <div className="mt-4">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">{data.engagementRate}%</span>
                    <TrendIndicator current={data.engagementRate} previous={0} />
                  </div>
                  <p className="text-sm opacity-90">Engagement Rate</p>
                  <div className="mt-2">
                    {getPerformanceLevel(data.engagementRate, platform)}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="bg-white bg-opacity-20 rounded-full h-2">
                    <div 
                      className="bg-white rounded-full h-2 transition-all duration-300"
                      style={{ 
                        width: `${Math.min((data.engagementRate / config.benchmark) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                  <p className="text-xs opacity-75 mt-1">
                    Benchmark: {config.benchmark}% (Excellent)
                  </p>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Views */}
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <Eye className="h-5 w-5 mx-auto text-gray-600 mb-1" />
                    <div className="text-lg font-bold text-gray-900">{data.views.toLocaleString()}</div>
                    <div className="text-xs text-gray-600">Views</div>
                    <TrendIndicator current={data.views} previous={previousData.views} />
                  </div>

                  {/* Likes */}
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <Heart className="h-5 w-5 mx-auto text-red-500 mb-1" />
                    <div className="text-lg font-bold text-gray-900">{data.likes.toLocaleString()}</div>
                    <div className="text-xs text-gray-600">Likes</div>
                    <TrendIndicator current={data.likes} previous={previousData.likes} />
                  </div>

                  {/* Comments */}
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <MessageCircle className="h-5 w-5 mx-auto text-blue-500 mb-1" />
                    <div className="text-lg font-bold text-gray-900">{data.comments.toLocaleString()}</div>
                    <div className="text-xs text-gray-600">Comments</div>
                    <TrendIndicator current={data.comments} previous={previousData.comments} />
                  </div>

                  {/* Shares */}
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <Share2 className="h-5 w-5 mx-auto text-green-500 mb-1" />
                    <div className="text-lg font-bold text-gray-900">{data.shares.toLocaleString()}</div>
                    <div className="text-xs text-gray-600">Shares</div>
                    <TrendIndicator current={data.shares} previous={previousData.shares} />
                  </div>
                </div>

                {/* Saves (Instagram/TikTok specific) */}
                {(platform === 'instagram' || platform === 'tiktok') && (
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <Bookmark className="h-5 w-5 mx-auto text-purple-500 mb-1" />
                    <div className="text-lg font-bold text-gray-900">{data.saves.toLocaleString()}</div>
                    <div className="text-xs text-gray-600">Saves</div>
                    <TrendIndicator current={data.saves} previous={previousData.saves} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Statistics */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {Object.values(platformAnalytics).reduce((sum, data) => sum + data.views, 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Views</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {Object.values(platformAnalytics).reduce((sum, data) => sum + data.likes + data.comments + data.shares + data.saves, 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Engagements</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {(Object.values(platformAnalytics).reduce((sum, data) => sum + data.engagementRate, 0) / 3).toFixed(2)}%
            </div>
            <div className="text-sm text-gray-600">Avg Engagement Rate</div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {Object.values(platformAnalytics).reduce((sum, data) => sum + data.posts, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Posts</div>
          </div>
        </div>
      </div>

      {/* Formula Explanation */}
      <div className="bg-blue-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Engagement Rate Formula</h3>
        <div className="text-sm text-blue-800">
          <p className="mb-2">
            <strong>Engagement Rate = (Likes + Comments + Shares + Saves) ÷ Views × 100</strong>
          </p>
          <p className="text-blue-700">
            This standardized formula provides consistent measurement across all social media platforms 
            and aligns with industry best practices for calculating engagement rates.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaAnalyticsDashboard;
